var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable } from "mobx";
import { VERIFICATION_DEFAULT_OTP_LENGTH, VERIFICATION_COOLDOWN_SECONDS, VerificationPurpose } from "healthex-common/verification.dtos";
import { FormModel } from "./FormModel";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
function maskPhone(phone) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 2)
        return "***-***-**??";
    const lastTwo = digits.slice(-2);
    return `***-***-**${lastTwo}`;
}
export var VerifyOTPState;
(function (VerifyOTPState) {
    VerifyOTPState["INITIAL"] = "INITIAL";
    VerifyOTPState["VERIFYING"] = "VERIFYING";
    VerifyOTPState["VERIFIED"] = "VERIFIED";
    VerifyOTPState["INCORRECT_CODE"] = "INCORRECT_CODE";
    VerifyOTPState["TOO_MANY_ATTEMPTS"] = "TOO_MANY_ATTEMPTS";
    VerifyOTPState["INVALID_US_PHONE_NUMBER"] = "INVALID_US_PHONE_NUMBER";
    VerifyOTPState["LOCKOUT"] = "LOCKOUT";
})(VerifyOTPState || (VerifyOTPState = {}));
export var SendOTPState;
(function (SendOTPState) {
    SendOTPState["INITIAL"] = "INITIAL";
    SendOTPState["SENDING"] = "SENDING";
    SendOTPState["DISABLED"] = "DISABLED";
    SendOTPState["SUCCESS"] = "SUCCESS";
    SendOTPState["ERROR"] = "ERROR";
})(SendOTPState || (SendOTPState = {}));
export var OTPClearLoginState;
(function (OTPClearLoginState) {
    OTPClearLoginState["DEFAULT"] = "DEFAULT";
    OTPClearLoginState["CLEAR_EXPIRED"] = "CLEAR_EXPIRED";
    OTPClearLoginState["CLEAR_ERROR"] = "CLEAR_ERROR";
    OTPClearLoginState["LOADING"] = "LOADING";
    OTPClearLoginState["INVALID_US_PHONE_NUMBER"] = "INVALID_US_PHONE_NUMBER";
})(OTPClearLoginState || (OTPClearLoginState = {}));
export function getVerifyOTPErrorMessage(state) {
    switch (state) {
        case VerifyOTPState.INCORRECT_CODE:
            return t("Incorrect code. Please try again.");
        case VerifyOTPState.TOO_MANY_ATTEMPTS:
            return t("Too many attempts. Please resend the code.");
        case VerifyOTPState.INVALID_US_PHONE_NUMBER:
            return t("Please enter a valid US phone number to continue");
        default:
            return null;
    }
}
export class VerifyOTPManager {
    constructor(authManager, verificationService, socialLoginAuthManager) {
        this.authManager = authManager;
        this.verificationService = verificationService;
        this.socialLoginAuthManager = socialLoginAuthManager;
        this.pendingVerificationKey = null;
        this.pendingVerificationPhone = null;
        this.formModel = new FormModel({
            verificationCode: "",
        }, ["verificationCode"]);
        this.verifyOTPState = VerifyOTPState.INITIAL;
        this.sendOTPState = SendOTPState.INITIAL;
        this.resendCooldownIntervalId = null;
        this.disableResendTimeoutId = null;
        this.pendingRateLimitCooldownSeconds = null;
        this.resendCooldownDurationSeconds = VERIFICATION_COOLDOWN_SECONDS;
        this.resendCooldownSecondsLeft = this.resendCooldownDurationSeconds;
    }
    get phoneVerified() {
        var _a;
        return (_a = this.socialLoginAuthManager.pendingPhoneVerified) !== null && _a !== void 0 ? _a : this.authManager.phoneVerified;
    }
    resetVerificationFlow() {
        this.verifyOTPState = VerifyOTPState.INITIAL;
        this.sendOTPState = SendOTPState.INITIAL;
        this.clearDisableResendTimeout();
        this.clearResendCooldownTimer();
        this.pendingRateLimitCooldownSeconds = null;
        this.resendCooldownSecondsLeft = this.resendCooldownDurationSeconds;
        this.formModel.updateInput({ verificationCode: "" }, false);
    }
    disableResendButtonAndStartCooldown() {
        var _a;
        this.clearDisableResendTimeout();
        this.sendOTPState = SendOTPState.DISABLED;
        const initialSeconds = (_a = this.pendingRateLimitCooldownSeconds) !== null && _a !== void 0 ? _a : this.resendCooldownDurationSeconds;
        this.pendingRateLimitCooldownSeconds = null;
        this.startResendCooldownTimer(initialSeconds);
    }
    clearDisableResendTimeout() {
        if (this.disableResendTimeoutId !== null) {
            window.clearTimeout(this.disableResendTimeoutId);
            this.disableResendTimeoutId = null;
        }
    }
    getMaskedPhoneNumber() {
        return maskPhone(this.mobilePhone);
    }
    validateVerificationCode() {
        if (!this.formModel.input.verificationCode) {
            return false;
        }
        const code = this.formModel.input.verificationCode.replace(/\D/g, "");
        return code.length === VERIFICATION_DEFAULT_OTP_LENGTH;
    }
    get input() {
        return this.formModel.input;
    }
    get mobilePhone() {
        var _a;
        if (this.pendingVerificationPhone) {
            return this.pendingVerificationPhone;
        }
        return (_a = this.authManager.phone) !== null && _a !== void 0 ? _a : "";
    }
    get sessionKey() {
        var _a, _b;
        return (_b = (_a = this.pendingVerificationKey) !== null && _a !== void 0 ? _a : this.authManager.id) !== null && _b !== void 0 ? _b : "";
    }
    async sendVerificationCode(isFirstSend) {
        var _a;
        if (this.sendOTPState === SendOTPState.SENDING) {
            return;
        }
        const phone = this.pendingVerificationKey ? (_a = this.pendingVerificationPhone) !== null && _a !== void 0 ? _a : "" : this.mobilePhone;
        if (!phone) {
            this.sendOTPState = SendOTPState.INITIAL;
            return;
        }
        if (!this.sessionKey) {
            this.sendOTPState = SendOTPState.INITIAL;
            return;
        }
        try {
            this.sendOTPState = SendOTPState.SENDING;
            const outcome = await this.verificationService.sendVerification(this.sessionKey, phone, VerificationPurpose.PATIENT_PHONE_NUMBER);
            if (outcome.ok) {
                this.sendOTPState = isFirstSend ? SendOTPState.DISABLED : SendOTPState.SUCCESS;
                this.verifyOTPState = VerifyOTPState.INITIAL;
                this.formModel.updateInput({ verificationCode: "" }, false);
                this.clearDisableResendTimeout();
            }
            else {
                this.sendOTPState = isFirstSend ? SendOTPState.DISABLED : SendOTPState.ERROR;
                if (outcome.code === "rate_limited" && typeof outcome.retryAfterSeconds === "number") {
                    this.pendingRateLimitCooldownSeconds = outcome.retryAfterSeconds;
                }
                else {
                    this.clearDisableResendTimeout();
                }
            }
        }
        catch (error) {
            console.error("Error sending verification code", error);
            this.clearDisableResendTimeout();
            this.sendOTPState = SendOTPState.ERROR;
        }
        finally {
            this.disableResendTimeoutId = window.setTimeout(() => this.disableResendButtonAndStartCooldown(), this.sendOTPState === SendOTPState.SUCCESS || this.sendOTPState === SendOTPState.ERROR ? 2000 : 0);
        }
    }
    async submitVerificationCode() {
        var _a;
        if (!this.formModel.input.verificationCode) {
            return;
        }
        const phone = this.pendingVerificationKey ? (_a = this.pendingVerificationPhone) !== null && _a !== void 0 ? _a : "" : this.mobilePhone;
        const code = this.formModel.input.verificationCode.replace(/\D/g, "");
        if (!phone || code.length !== VERIFICATION_DEFAULT_OTP_LENGTH) {
            return;
        }
        if (!this.sessionKey) {
            return;
        }
        this.verifyOTPState = VerifyOTPState.VERIFYING;
        try {
            const outcome = await this.verificationService.submitVerificationCode(this.sessionKey, phone, code, VerificationPurpose.PATIENT_PHONE_NUMBER);
            if (outcome.ok) {
                this.verifyOTPState = VerifyOTPState.VERIFIED;
            }
            else {
                switch (outcome.code) {
                    case "too_many_attempts":
                        this.verifyOTPState = VerifyOTPState.TOO_MANY_ATTEMPTS;
                        break;
                    case "invalid_or_expired":
                        this.verifyOTPState = VerifyOTPState.INCORRECT_CODE;
                        break;
                    case "invalid_us_phone_number":
                        this.verifyOTPState = VerifyOTPState.INVALID_US_PHONE_NUMBER;
                        break;
                    default:
                        this.verifyOTPState = VerifyOTPState.INCORRECT_CODE;
                }
            }
        }
        catch (error) {
            console.error("Error verifying code", error);
            this.verifyOTPState = VerifyOTPState.INCORRECT_CODE;
        }
    }
    startResendCooldownTimer(initialSeconds = this.resendCooldownDurationSeconds) {
        this.clearResendCooldownTimer();
        this.resendCooldownSecondsLeft = initialSeconds;
        this.resendCooldownIntervalId = window.setInterval(() => {
            this.resendCooldownSecondsLeft = Math.max(0, this.resendCooldownSecondsLeft - 1);
            if (this.resendCooldownSecondsLeft === 0) {
                this.clearResendCooldownTimer();
                this.sendOTPState = SendOTPState.INITIAL;
            }
        }, 1000);
    }
    clearResendCooldownTimer() {
        if (this.resendCooldownIntervalId !== null)
            window.clearInterval(this.resendCooldownIntervalId);
        this.resendCooldownIntervalId = null;
    }
}
__decorate([
    observable
], VerifyOTPManager.prototype, "pendingVerificationKey", void 0);
__decorate([
    observable
], VerifyOTPManager.prototype, "pendingVerificationPhone", void 0);
__decorate([
    observable
], VerifyOTPManager.prototype, "verifyOTPState", void 0);
__decorate([
    observable
], VerifyOTPManager.prototype, "sendOTPState", void 0);
__decorate([
    observable
], VerifyOTPManager.prototype, "resendCooldownSecondsLeft", void 0);
__decorate([
    computed
], VerifyOTPManager.prototype, "phoneVerified", null);
__decorate([
    computed
], VerifyOTPManager.prototype, "mobilePhone", null);
